
export type Party = "caller" | "interpreter" | "agent";
export type Direction = "inbound" | "outbound";

export type SegmentStatus =
  | "streaming-transcription"
  | "awaiting-interpretation"
  | "streaming-interpretation"
  | "finalized";

export interface NormalizedEvent {
  kind: "transcript" | "interpretation";
  callId?: string;
  party: Party;
  direction?: Direction;

  segmentId?: string;
  orderSeq?: number;

  rawId?: string;
  turnId?: string;
  isLive?: boolean;
  isFinal?: boolean;
  revision?: number;
  displaySeq?: number;
  original?: string;
  english?: string;
  sourceLang?: string;
  targetLang?: string;
  isTranslationUpdate?: boolean;
  source?: string;
  optimistic?: boolean;
  createdAt?: string | number;
  aiInterpreter?: boolean;
  historical?: boolean;
  historyOrder?: number;

  seq?: number;
  chunk?: string;
  done?: boolean;
  utteranceEnd?: boolean;
}

/** Which source last populated a segment's English interpretation. */
export type EnglishSource = "none" | "delta" | "mt";

export interface SegmentState {
  segId: string;
  cardId: string;
  party: Party;
  direction: Direction;
  arrival: number;
  orderSeq: number;
  createdAt?: string | number;
  sourceLang: string;
  targetLang: string;

  rolling: string;
  interp: string;
  englishSource: EnglishSource;

  chunks: Map<number, string>;
  interpStarted: boolean;
  interpFinal: boolean;

  rollingRevision: number;
  hasRolling: boolean;
  rollingFinal: boolean;

  status: SegmentStatus;
  finalized: boolean;
  hasRealId: boolean;
  source?: string;
  aiInterpreter?: boolean;
  historical: boolean;
}

export interface CardState {
  id: string;
  party: Party;
  direction: Direction;
  sourceLang: string;
  orderSeq: number;
  createdAt?: string | number;
  aiInterpreter: boolean;
  historical: boolean;
  segIds: string[];
}

export interface StreamState {
  cardOrder: string[];
  cards: Record<string, CardState>;
  segments: Record<string, SegmentState>;
  bindKeyToSeg: Record<string, string>;
  lastCardId: string | null;
  pendingTypeToTalkSeg: string | null;
  activeSegId: string | null;
  arrivalCounter: number;
  segCounter: number;
  cardCounter: number;
  callId?: string;
}

export const PARTIES: Party[] = ["caller", "interpreter", "agent"];

export function createInitialState(callId?: string): StreamState {
  return {
    cardOrder: [],
    cards: {},
    segments: {},
    bindKeyToSeg: {},
    lastCardId: null,
    pendingTypeToTalkSeg: null,
    activeSegId: null,
    arrivalCounter: 0,
    segCounter: 0,
    cardCounter: 0,
    callId
  };
}

export function langBucket(lang?: string): string {
  return (lang || "").slice(0, 2).toLowerCase();
}

export function isForeignLang(lang?: string): boolean {
  const l = langBucket(lang);
  return !!l && l !== "en";
}

export function isEnglishLang(lang?: string): boolean {
  const l = langBucket(lang);
  return !l || l === "en";
}
